//TODO : Refactor after ai
import type { ReactNode } from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@app/providers";
import { useLanguage, useTranslation } from "@shared/locales";
import type { LanguageTypes } from "@shared/locales";

interface AuthShellProps {
  title: string;
  description: string;
  /** Already-translated server failure, rendered as a banner above the form. */
  serverError?: string | null;
  children: ReactNode;
  /** Secondary line under the card, linking to the other auth screens. */
  footer?: ReactNode;
}

/** Cycled by the shell's language button; mirrors the Settings page's list. */
const LANGUAGES: LanguageTypes[] = ["en", "de", "ar"];

/**
 * Chrome shared by every auth screen: centered card on the page surface,
 * wordmark, language + theme controls, and the server-error banner slot.
 */
export function AuthShell({
  title,
  description,
  serverError,
  children,
  footer,
}: AuthShellProps) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();

  const currentIndex = LANGUAGES.indexOf(language);
  const nextLanguage = LANGUAGES[(currentIndex + 1) % LANGUAGES.length];

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background p-8">
      <div className="absolute end-4 top-4 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => void setLanguage(nextLanguage)}
          className="inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label={t("auth.shell.changeLanguage")}
        >
          {language?.toUpperCase()}
        </button>
        <button
          type="button"
          onClick={toggleTheme}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label={t("auth.shell.toggleTheme")}
        >
          {theme === "dark" ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </button>
      </div>

      <div className="w-full max-w-[360px]">
        <div className="mb-8 flex flex-col items-center">
          {/* Viacid-Wortmarke (Prototyp) */}
          <img src="/viacid/logo.svg" alt="Viacid" style={{ height: 36 }} />
          <span className="mt-2 text-[13px] text-muted-foreground">
            Prototyp Betriebssoftware · Beispieldaten · Build {__BUILD_STAMP__}
          </span>
        </div>

        <div className="rounded-lg border border-border bg-card p-7 shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight text-card-foreground">
            {title}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>

          {serverError ? (
            <div
              role="alert"
              className="mt-5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <div className="mt-6">{children}</div>

          {footer ? (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              {footer}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
